<script>
    import { slide, fly } from 'svelte/transition';
    import { Tooltip } from 'sveltestrap';
    import { tooltip } from "@svelte-plugins/tooltips";

    import { formatDate } from '../../date-time.js';
    import { getArrivalDateAndTimeTooltip } from '../../inmate.js'
    import { vptServer, token, inmateDrawerOpen, getTooltipDisplayOptions } from '../../stores.js';
    import { referenceDataEarlyRiskTabList } from "../../reference-data.js";
    import { getEarlyRiskColourClass, getEarlyRiskColourName, getEarlyRiskTooltip } from "../../early-risk.js"

    import AimTooltip from '../../component/AimTooltip.svelte';

    export let id;
    export let todaysConfig;

    let history = [];
    let tabbedHistory = [];
    let searchCompleted = false;
    let selectedStatus;
    let selectedComment = null;

    $: {
        if (todaysConfig) {
            history = null;
            getEarlyRiskHistory();
        }
    }

    async function getEarlyRiskHistory() {
        searchCompleted = false;
        history = [];
        tabbedHistory = [];
        
        await fetch(`${$vptServer}/inmate/${id}/earlyrisk/list`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            let firstCard;
            // Create deep copy of data to avoid mutating the original
            history = data.slice(0);
            // Process the history of assessments into a list of tabbed cards
            data.forEach((assessment,index,arr) => {
                if (firstCard == null) firstCard = assessment.assessmentUrn;
                // Overwrite the original assessment in the array, with a new object containing the tabbed answers and other properties
                arr[index] = {...assessment,
                    ...getTabbedAnswers(assessment.checklistUrn, assessment.assessmentAnswers, assessment.assessmentTabs),
                    answerGroups : groupAnswers(assessment.assessmentAnswers),
                    active: firstCard == assessment.assessmentUrn ? true : false};
            });
            tabbedHistory = data;
        })
        .catch(error => {
            console.error("Error getting early risk history");
            console.error(error);
            return;
        });
        searchCompleted = true;
    }

    function getTabbedAnswers(checklistUrn, assessmentAnswers, assessmentTabs) {
        // If the mapping of checklist URN to assessment tabs is empty, log an error and return
        if (!assessmentTabs || assessmentTabs.length == 0) { 
            console.error("No assessment tabs found for checklistUrn: " + checklistUrn); 
            return; 
        }

        let tabs = referenceDataEarlyRiskTabList
        // Filter the list of Early Risk tabs to only those that relate to the completed checklist 
        .filter(tab => tab.checklistUrn == checklistUrn)
        // Then map the filtered list and create a new object for each tab containing the tab's ID, name, and answers
        .map(tab => {
            let start = tab.questionStart;
            let end = tab.questionFinish;

            // Filter the assessment answers to only those that are within the range of the current tab's start and end questions
            let answers = assessmentAnswers.filter(answer => {
                return (answer.section == start || answer.section == end || (answer.section > start && answer.section < end));
            })
            .map(a => {
                return {question : a.question, questionHelpText : a.questionHelpText, questionHelpTextAnswerTrigger : a.questionHelpTextAnswerTrigger, answer : a.answer, notes : a.notes, status : a.ragstatus, order : a.questionNumber, section : a.section, header : a.sectionHeader};
            });

            return { id : tab.vptEarlyRiskTabUrn, name : tab.name, answers : splitAnswers(answers), order : tab.ordering, 
                active : tab.ordering == 1, targeted : assessmentTabs.find((t) => t.vptEarlyRiskTabUrn == tab.vptEarlyRiskTabUrn).targetedOnTab }
        });

        return { tabs : tabs, displayedAnswers : tabs.find((t) => t.active).answers }
    }

    // Splits the answers into two columns if there are more than 4 answers, otherwise returns a single column
    function splitAnswers(answers) {
        if (answers.length > 4) {
            let colLength = answers.length / 2;
            colLength += answers.length % 2;

            let firstColumn = answers.slice(0, colLength);
            let secondColumn = answers.slice(colLength, answers.length + 1);
        
            // If the last item in the first column is a header, move it to the start of the second column
            if (firstColumn[firstColumn.length - 1].header) {
                secondColumn.unshift(firstColumn.pop());
            }

            return [firstColumn, secondColumn]
        } else {
            return [answers, new Array(0)];
        }
    }
    
    // Groups the answers by their RAG status and calculates the total and percentage of each group
    function groupAnswers(assessmentAnswers) {
        let totalAnswers = 0;
        let answerMap = new Map();

        assessmentAnswers.forEach((assessmentAnswer) => {
            if (assessmentAnswer.ragstatus > 0) {
                if (answerMap.has(assessmentAnswer.ragstatus)) {
                    answerMap.set(assessmentAnswer.ragstatus, (answerMap.get(assessmentAnswer.ragstatus) + 1));
                } else {
                    answerMap.set(assessmentAnswer.ragstatus, 1);
                }
                totalAnswers++;
            }
        });
        
        let answerMapSorted = new Map([...answerMap].sort().reverse());

        let answerGroups = [];
        answerMapSorted.forEach((v, k) => {
            answerGroups.push({status : k, total : v, percentage : Math.round((v / totalAnswers) * 100)});
        });

        return answerGroups;
    }

    function selectTab(assessment, selectedId) {
        assessment.tabs.forEach((tab) => {
            if (tab.id === selectedId) {
                tab.active = true;
            } else {
                tab.active = false;
            }
        });

        assessment.displayedAnswers = assessment.tabs.find((t) => t.active).answers;

        tabbedHistory = tabbedHistory;
    }
</script>

<style>
    .container-fluid {
        padding-top: 163px;
        min-width: 1100px;
    }

    .container-fluid {
        padding-right: 96px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }

    .container-fluid-short {
        padding-right: 270px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }
    
    .card {
        margin-top: 5px;
    }

    .card-head {    
    	padding: 16px 30px 10px 30px;
        border-bottom: none;
    }

    .card-head.collapsed {
        padding-bottom: 16px;
    }

    .card-head .target-indicator {
        position: absolute;
        height: 16px;
        width: 16px;
        left: 8px;
    }

    .arrival-date {
        margin-left: 5px;
        margin-right: 5px;
    }

    .progress-time {
        width: 82px;
        height: 15px;
        margin-left: 10px;
    }

    .subtitle-assessment {
        margin-left: 20px;
    }

    .selected-row {
        font-weight: 600;
    }

    .hidden-row {
        filter: opacity(30%);
    }

    .modal-header {
        padding-top: 10px;
        padding-bottom: 10px;
    }

    .modal h5 {
        font-size: 18px;
    }

    .modal-body {
        padding: 0;
    }

    .modal-body > div {
        padding: 1rem;
    }

    .modal-body .prompt {
        background-color: #edf3f7;
    }

    .modal-footer {
        padding: 0.5rem;
    }
</style>

<div class="container-fluid" class:container-fluid-short={$inmateDrawerOpen}>
    {#if searchCompleted}
        {#if history.length > 0}
            {#each tabbedHistory as assessment}
                <div class="row">
                    <div class="col-sm-12">
                        <div class="card tabbed-card">
                            <button on:click="{() => {assessment.active = !assessment.active; assessment = assessment;}}"
                                class="button-reset focus-outline card-head" class:collapsed={!assessment.active}>

                                {#if assessment.indicatorTargeted}
                                    <img id="target-indicator-{assessment.assessmentUrn}" class="target-indicator" src="/assets/target-indicator.svg" alt="{todaysConfig.inmateLabelName} was targeted on this assessment" />
                                    <Tooltip target="target-indicator-{assessment.assessmentUrn}" placement="bottom">{todaysConfig.inmateLabelName} was targeted on this assessment.</Tooltip>
                                {/if}

                                <span use:tooltip={{
                                    content: {
                                        component: AimTooltip,
                                        props: getArrivalDateAndTimeTooltip(assessment)
                                    },
                                    ...getTooltipDisplayOptions()
                                    }}>
                                    {#if assessment.indicatorEarlyRiskPeriod}
                                        {#if assessment.hoursSinceArrival == 0}
                                            Arrived in the last hour
                                        {:else}
                                            Arrived {assessment.hoursSinceArrival} hour{assessment.hoursSinceArrival == 1 ? '' : 's'} ago
                                        {/if}
                                    {:else}
                                        Arrived {formatDate(assessment.dateArrived)}
                                    {/if}
                                </span>

                                {#if assessment.indicatorEarlyRiskPeriod}
                                    <div class="progress progress-time" id="time-{assessment.assessmentUrn}">
                                        <div class="progress-bar progress-bar-primary" role="progressbar" 
                                            style="width: {100 -((assessment.hoursSinceArrival / todaysConfig.earlyRiskNumberOfHoursPeriod) * 100)}%"
                                            aria-valuenow="{100 -((assessment.hoursSinceArrival / todaysConfig.earlyRiskNumberOfHoursPeriod) * 100)}" 
                                            aria-valuemin="0" aria-valuemax="100">
                                        </div>
                                        <Tooltip target="time-{assessment.assessmentUrn}" placement="bottom">
                                            {todaysConfig.earlyRiskNumberOfHoursPeriod - assessment.hoursSinceArrival} of {todaysConfig.earlyRiskNumberOfHoursPeriod} hours remaining in the Early Risk period
                                        </Tooltip>
                                    </div>
                                {/if}

                                {#if !assessment.indicatorCompleted}
                                    <div class="subtitle-assessment">
                                        <img src="/assets/exclamation-danger.svg" alt="{todaysConfig.earlyRiskAssessmentLabelName} has not been completed" />
                                        <span>The {todaysConfig.earlyRiskAssessmentLabelName} has not been completed {assessment.nonCompletionReason !== "" ? '(' + assessment.nonCompletionReason.trim() + ')' : ''}</span>
                                    </div>
                                {:else if assessment.indicatorMarkedAsCompleted}
                                    <div class="subtitle-assessment">
                                        <span>The {todaysConfig.earlyRiskAssessmentLabelName} was manually marked as completed {assessment.nonCompletionReason !== "" ? '(' + assessment.nonCompletionReason + ')' : ''}</span>
                                    </div>
                                {/if}

                                <div class="card-progress-container ml-auto">
                                    <div class="progress">
                                        {#each assessment.answerGroups as answerGroup}
                                            <button class="button-reset focus-outline progress-bar {getEarlyRiskColourClass(answerGroup.status)}"
                                                    on:click|stopPropagation={() => (selectedStatus === answerGroup.status) ? selectedStatus = "" : selectedStatus = answerGroup.status} 
                                                    id="answers-{assessment.assessmentUrn}-{answerGroup.status}"
                                                    style="width: {answerGroup.percentage}%">
                                                <span>{answerGroup.total}</span>
                                            </button>
                                            <Tooltip target="answers-{assessment.assessmentUrn}-{answerGroup.status}" placement="bottom">
                                                {getEarlyRiskColourName(answerGroup.status)} ({answerGroup.percentage}%)
                                            </Tooltip>
                                        {/each}
                                    </div>
                                </div>

                                {#if assessment.active}
                                    <img class="card-collapse-control" src="/assets/arrow/chevron-down.svg" alt="Collapse Card" />
                                {:else}
                                    <img class="card-collapse-control" src="/assets/arrow/chevron-right.svg" alt="Expand Card" />
                                {/if}
                            </button>

                            {#if assessment.active}
                                <div transition:slide|local class="card-body">

                                    <nav>
                                        <div class="nav nav-tabs" id="nav-tab" role="tablist">
                                            {#each assessment.tabs as tab}
                                                <button on:click={selectTab(assessment, tab.id)} role="tab" aria-selected="{tab.active}"
                                                        class="nav-link focus-outline" class:active={tab.active} type="button">
                                                    {#if tab.targeted}
                                                        <img id="tab-{assessment.assessmentUrn}-{tab.id}" src="/assets/exclamation-triangle-danger.svg" alt="{todaysConfig.earlyRiskFlagDescription} generated in this section" />
                                                        <Tooltip target="tab-{assessment.assessmentUrn}-{tab.id}" placement="bottom">
                                                            {todaysConfig.earlyRiskAssessmentLabelName} indicated {tab.name} as a possible risk factor
                                                        </Tooltip>
                                                    {/if}
                                                    {tab.name}
                                                </button>
                                            {/each}
                                        </div>
                                    </nav>

                                    <div class="card-columns-container">
                                        <div class="card-columns">
                                            {#each assessment.displayedAnswers as answers}
                                                <div class="card-column">
                                                    {#each answers as answer}
                                                        <div class="card-column-row" class:card-column-section-header={answer.header}
                                                                class:selected-row="{selectedStatus && answer.status == selectedStatus}"
                                                                class:hidden-row="{selectedStatus && answer.status != selectedStatus}">
                                                            {#if answer.header}
                                                                <span>{answer.question}</span>
                                                            {:else}
                                                                <span>{answer.question}</span>

                                                                <div class="icon-container">
                                                                    <span class:text-danger={answer.status == 3} class:text-warning={answer.status == 2}
                                                                            class:text-success={answer.status == 1} class:icon-offset={answer.notes && answer.notes != 'No comments'}>
                                                                        {answer.answer}
                                                                    </span>
                                                                    {#if answer.notes && answer.notes != 'No comments'}
                                                                        <button id="notes-{assessment.assessmentUrn}-{answer.section}-{answer.order}" type="button" class="button-reset focus-outline"
                                                                        on:click={() => selectedComment = answer}
                                                                        use:tooltip={{
                                                                            content: {
                                                                                component: AimTooltip ,
                                                                                props: getEarlyRiskTooltip(answer)
                                                                            },
                                                                            ...getTooltipDisplayOptions({ position: "left", maxWidth: 600 })
                                                                        }}>
                                                                            <img src="/assets/notes-fill.svg" alt="{answer.notes}" />
                                                                        </button>
                                                                    {/if}
                                                                </div>
                                                            {/if}
                                                        </div>
                                                    {/each}
                                                </div>
                                            {/each}
                                        </div>
                                    </div>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            {/each}
        {:else}
            <div class="alert alert-danger" role="alert">No Risk Screening Reception Assessments have been started.</div>
        {/if}
    {:else}
        <div class="spinner-container">
            <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
            <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
        </div>
    {/if}
</div>

{#if selectedComment}
    <div class="modal" tabindex="-1" role="dialog" aria-label="Comments">
        <div in:fly|local={{ y: -100 }} class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Comments</h5>
                    
                    <button type="button" class="close" aria-label="Close" on:click={() => selectedComment = null}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <div class="modal-body">
                    {#if (selectedComment.answer == selectedComment.questionHelpTextAnswerTrigger) && selectedComment.questionHelpText}
                        <div class="prompt">
                            {selectedComment.questionHelpText}
                        </div>
                    {/if}

                    <div>
                        <span>{selectedComment.notes}</span>
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" on:click={() => selectedComment = null}>Close</button>
                </div>
            </div>
        </div>
    </div>

    <div class="modal-backdrop show"></div>
{/if}